import classNames from 'classnames';
import { useField } from 'formik';
import type React from 'react';
import { forwardRef } from 'react';
import type { Colors } from '../../../constants';
import { TextCaption } from '../../Typography';
import { InputText } from '../InputText';
import type { InputTextProps } from '../InputText';
import { RequiredAsterisk } from '../RequiredAsterix';

export interface InputTextFieldProps
  extends Pick<
    InputTextProps,
    | 'value'
    | 'placeholder'
    | 'className'
    | 'disabled'
    | 'multiline'
    | 'multilineOptions'
    | 'readOnly'
    | 'ellipsis'
    | 'password'
    | 'defaultValue'
    | 'showCharsCounter'
    | 'state'
    | 'leftElement'
    | 'rightElement'
    | 'autoFocus'
    | 'maxLength'
    | 'onBlur'
    | 'onFocus'
    | 'onKeyUp'
    | 'onKeyDown'
    | 'onKeyPress'
    | 'onChange'
  > {
  name: string;
  label?: string;
  inputSize?: InputTextProps['size'];
  error?: boolean;
  statusText?: string;
  required?: boolean;
  noLabels?: boolean;
  validateFieldOnBlur?: boolean;
  clearOnFocus?: boolean;
  hint?: string;
  hintCustomClass?: string;
  invalid?: boolean | string;
  children?: React.ReactNode;
  noAddedHeight?: boolean;
  dataDsId?: string;
  withSuccessIndication?: boolean;
}

export const containerClasses = 'flex flex-col relative w-full';

export const labelClasses = {
  sm: 'text-ds-natural-300',
  md: 'text-ds-accent2-500',
  lg: 'text-ds-danger-400',
};
export const statusClasses = {
  normal: 'text-ds-natural-300',
  success: 'border-ds-accent2-400',
  error: 'text-ds-danger-400',
};

export const InputTextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputTextFieldProps
>(
  (
    {
      inputSize = 'md',
      label,
      noLabels,
      required,
      disabled,
      placeholder,
      multiline,
      multilineOptions,
      defaultValue,
      validateFieldOnBlur,
      clearOnFocus,
      autoFocus,
      onBlur,
      invalid,
      onKeyDown,
      noAddedHeight,
      dataDsId,
      onChange,
      hint,
      children,
      state,
      hintCustomClass,
      onFocus,
      password,
      rightElement,
      leftElement,
      withSuccessIndication,
      showCharsCounter = false,
      maxLength,
      readOnly,
      ellipsis,
      ...props
    },
    ref
  ) => {
    const [field, meta, helpers] = useField(props);
    const isTouched = meta.touched;
    if (invalid && !meta.error) {
      helpers.setError(invalid as string);
    }
    const hasError = validateFieldOnBlur ? meta.error : meta.error && isTouched;
    const inputState = hasError
      ? 'error'
      : isTouched && meta.value?.length > 0 && withSuccessIndication
        ? 'success'
        : 'normal';

    return (
      <div
        className={classNames(containerClasses, {
          'h-20': !noAddedHeight,
        })}
      >
        {!noLabels && (
          <div className={classNames(labelClasses[inputSize])}>
            <label htmlFor={field.name}>
              <TextCaption
                label={label}
                color={labelClasses[inputState]}
              />
            </label>
            {required && <RequiredAsterisk />}
          </div>
        )}
        <InputText
          ref={ref}
          onBlur={(event) => {
            validateFieldOnBlur && !field.value && required
              ? helpers.setError('Required field')
              : field.onBlur?.(event);
            onBlur?.(event);
          }}
          onKeyDown={onKeyDown}
          onFocus={
            onFocus ||
            (() => {
              if (clearOnFocus) {
                helpers.setError(undefined);
                helpers.setTouched(false);
              }
            })
          }
          defaultValue={defaultValue}
          multiline={multiline}
          multilineOptions={multilineOptions}
          state={state ? state : inputState}
          size={inputSize}
          disabled={disabled}
          name={field.name}
          value={field.value ?? ''}
          onChange={(e) => {
            onChange?.(e);
            field.onChange(e);
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          dataDsId={dataDsId}
          password={password}
          rightElement={rightElement}
          leftElement={leftElement}
          showCharsCounter={showCharsCounter}
          maxLength={maxLength}
          readOnly={readOnly}
          ellipsis={ellipsis}
        />
        {!noLabels && hasError && (
          <div
            className={classNames('absolute w-full', {
              'top-7': !label,
              'bottom-0': label,
            })}
          >
            {hasError && (
              <div className="whitespace-nowrap">
                <TextCaption
                  label={meta?.error || ''}
                  color={statusClasses[inputState] as Colors}
                  lineHeight="leading-ds-100"
                />
              </div>
            )}
          </div>
        )}

        {!noLabels && children && <div>{children}</div>}

        {!noLabels && hint && (
          <div
            className={classNames('mt-1 absolute w-full', {
              'top-[28px]': !label,
              'top-[67px]': label,
            })}
          >
            <div className={classNames(hintCustomClass)}>
              <TextCaption label={hint} />
            </div>
          </div>
        )}
      </div>
    );
  }
);

InputTextField.displayName = 'InputTextField';
